import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { sendEmailNotification } from 'src/store/actions/Messages';
import { selectAuthenticatedUser } from 'src/store/selectors/auth';
import { upload } from 'src/helpers/upLoadHelper';
import { PATHS } from 'src/common/constants';
import { Messages } from 'src/components/Messages/Messages';
import { ROLES } from 'src/utils/MessagingUtils/shared-constants';
import {
  useServiceRequestActionsFromContext,
  useServiceRequestDataFromContext,
} from 'src/pages/ServiceRequest/Context/ServiceRequestContext';
import Loader from 'src/components/Loader';

interface ServiceRequestMessagesProps {
  serviceRequestId: any;
  ticketNumber: any;
  url: any;
  history: any;
}

interface MessageRecipient {
  id: string | null;
  error?: string;
}

const ServiceRequestMessages = ({
  serviceRequestId,
  ticketNumber,
  url,
  history,
}: ServiceRequestMessagesProps) => {
  const dispatch = useDispatch();
  const user = useSelector(selectAuthenticatedUser);
  const { serviceRequest, messages, loadingMessages } =
    useServiceRequestDataFromContext();
  const { postMessageToAPI } = useServiceRequestActionsFromContext();

  const getMessageRecipient = (): MessageRecipient => {
    const userRole = user?.roles?.[0]?.role?.name;

    if (!userRole) {
      return {
        id: null,
        error: 'User role not found. Please contact support.',
      };
    }

    if (!serviceRequest) {
      return {
        id: null,
        error:
          'Service request data not available. Please refresh and try again.',
      };
    }

    switch (userRole) {
      case ROLES.PROPERTY_DEVELOPER:
        return getPropertyDeveloperRecipient();
      default:
        return getDefaultRecipient();
    }
  };

  const getPropertyDeveloperRecipient = (): MessageRecipient => {
    const reporterId = serviceRequest.reporter?.id;
    const homeOwnerId = serviceRequest.suite?.homeOwner?.id;
    const currentUserId = user.id;

    if (!reporterId) {
      return {
        id: null,
        error:
          'No reporter found for this service request. Unable to send message.',
      };
    }

    // If current user is the reporter, send to homeowner.
    if (reporterId === currentUserId) {
      if (!homeOwnerId) {
        return {
          id: null,
          error:
            'No homeowner found for this service request. Unable to send message.',
        };
      }
      return { id: homeOwnerId };
    }

    // Otherwise, send to the reporter.
    return { id: reporterId };
  };

  const getDefaultRecipient = (): MessageRecipient => {
    const propertyManagers =
      serviceRequest.suite?.property?.propertyManagers || [];

    if (propertyManagers.length === 0) {
      return {
        id: null,
        error:
          'No property manager assigned to this property. Please contact your admin to have a property manager assigned before sending messages.',
      };
    }

    const primaryPropertyManager = propertyManagers[0];
    if (!primaryPropertyManager?.id) {
      return {
        id: null,
        error: 'Property manager data is invalid. Please contact support.',
      };
    }

    return { id: primaryPropertyManager.id };
  };

  const extractFileName = (imageUrl: string): string => {
    const urlParts = imageUrl.split('.com/');
    return urlParts.length > 1 ? urlParts[1] : '';
  };

  const onPostMessage = async (chat: any): Promise<void> => {
    try {
      // Handle image upload if present.
      let imageUrl: string | null = null;

      if (chat.image?.length > 0) {
        imageUrl = await upload(chat.image[0]);
      }

      const recipient: MessageRecipient = getMessageRecipient();

      if (!recipient.id) {
        toast.error(
          recipient.error || 'Unable to send message. Please try again.',
          {
            autoClose: 5000,
            position: 'top-center',
          }
        );
        return;
      }

      // Prepare message data.
      const messageData = {
        SentBy: user.id,
        SentTo: recipient.id,
        ReferenceId: serviceRequest.id,
        ReferenceType: 'Ticket',
        Message: chat.message,
        FileName: imageUrl ? extractFileName(imageUrl) : '',
        FileURL: imageUrl,
        TicketNumber: serviceRequest.ticketId?.toString() || '',
        ActionUrl: `app/service-requests/${serviceRequest.id}/activeTab=1`,
      };

      await postMessageToAPI(messageData);
    } catch (error) {
      console.error('Error posting message:', error);
      toast.error('Failed to send message. Please try again.', {
        autoClose: 5000,
        position: 'top-center',
      });
    }
  };

  const onSendEmailNotification = (data: any): void => {
    try {
      const chatId = serviceRequest.ticketId;
      const senderName = data.sentByName?.replace(/\s/g, '') || '';

      const navigationPath = {
        pathname: PATHS.VIDEO_CHAT,
        state: {
          chatId: `channel${chatId}`,
          sentByName: data.sentByName,
          sentToName: data.sentToName,
          url: url,
          activeTab: 1,
        },
      };

      const emailParams = {
        SentBy: data.sentTo,
        SentTo: data.sentBy,
        ChannelName: `channel${serviceRequest.ticketId}`,
        ActionUrl: `app/videoChat/chatId=channel${chatId}/${senderName}`,
        PropertyName: serviceRequest?.suite?.property?.name || '',
        SuiteName:
          serviceRequest.requestType === 'CommonArea'
            ? serviceRequest?.commonArea?.name || ''
            : serviceRequest?.suite?.number || '',
      };

      dispatch(
        sendEmailNotification(emailParams, navigationPath, history) as any
      );
    } catch (error) {
      console.error('Error sending email notification:', error);
      toast.error('Failed to send email notification. Please try again.', {
        autoClose: 5000,
        position: 'top-center',
      });
    }
  };

  if (loadingMessages) {
    return <Loader />;
  }

  return (
    <>
      <Messages
        serviceRequestId={serviceRequestId}
        ticketId={ticketNumber}
        url={url}
        messages={messages}
        onPostMessage={onPostMessage}
        onSendEmailNotification={onSendEmailNotification}
      />
    </>
  );
};

export default ServiceRequestMessages;
